import * as React from 'react'
import { LoaderCircle } from 'lucide-react'
import { cn } from '../lib/utils'

export type PosCommandActionVariant =
  | 'primary'
  | 'secondary'
  | 'channel'
  | 'warning'
  | 'utility'
  | 'exit'

export type PosCommandActionLabelVisibility = 'always' | 'desktop' | 'wide' | 'never'

export interface PosCommandActionProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  icon: React.ReactNode
  label: React.ReactNode
  status?: React.ReactNode
  variant?: PosCommandActionVariant
  active?: boolean
  loading?: boolean
  permitted?: boolean
  compact?: boolean
  labelVisibility?: PosCommandActionLabelVisibility
}

/**
 * Shared action primitive for dense operational command bars.
 * It keeps accessible names and loading/disabled behavior consistent while
 * allowing each surface to decide which action is currently primary.
 */
export const PosCommandAction = React.forwardRef<HTMLButtonElement, PosCommandActionProps>(
  (
    {
      icon,
      label,
      status,
      variant = 'secondary',
      active,
      loading = false,
      permitted = true,
      compact = false,
      labelVisibility = 'wide',
      className,
      disabled,
      title,
      type = 'button',
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    if (!permitted) return null

    const derivedLabel = typeof label === 'string' ? label : undefined
    const accessibleLabel = ariaLabel ?? title ?? derivedLabel

    return (
      <button
        ref={ref}
        type={type}
        title={title ?? derivedLabel}
        className={cn(
          'pos-command-action',
          `pos-command-action--${variant}`,
          `pos-command-action--label-${labelVisibility}`,
          active && 'is-active',
          loading && 'is-loading',
          compact && 'is-compact',
          className
        )}
        aria-label={accessibleLabel}
        aria-busy={loading || undefined}
        aria-pressed={active === undefined ? undefined : active}
        disabled={disabled || loading}
        {...props}
      >
        <span className="pos-command-action__icon" aria-hidden="true">
          {loading ? <LoaderCircle className="animate-spin" /> : icon}
        </span>
        {!compact && (
          <span className="pos-command-action__copy">
            <span className="pos-command-action__label">{label}</span>
            {status ? <span className="pos-command-action__status">{status}</span> : null}
          </span>
        )}
      </button>
    )
  }
)

PosCommandAction.displayName = 'PosCommandAction'
